import {
  EVENT_TRANSLATION_CREDIT_COST,
  type EventDescriptionLanguage,
  type EventDescriptionTranslations,
} from '@pulsify/shared/dto'
import { shallow, useStore } from '@tanstack/react-store'
import { RefreshCw, Sparkles } from 'lucide-react'
import { useId, useState } from 'react'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useTranslateDescription } from '~/features/ai-studio/hooks'
import { CreditCostTag } from '~/features/shared/components/credit-cost'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { richTextToPlainText } from '~/shared/lib/rich-text'
import { Button } from '~/shared/ui/button'
import { Label } from '~/shared/ui/label'
import { PulsifyLoader } from '~/shared/ui/pulsify-loader'
import { RichTextEditor } from '~/shared/ui/rich-text-editor'
import { SegmentedControl } from '~/shared/ui/segmented-control'
import { Switch } from '~/shared/ui/switch'

interface DescriptionFormState {
  values: {
    description?: string
    descriptionLanguage?: EventDescriptionLanguage
    descriptionTranslations?: EventDescriptionTranslations
  }
}

const LANGUAGE_COPY: Record<
  EventDescriptionLanguage,
  { name: () => string; also: () => string; heading: () => string }
> = {
  de: {
    name: m.eventDescriptionLanguageDe,
    also: m.eventDescriptionAlsoDe,
    heading: m.eventDescriptionInDe,
  },
  en: {
    name: m.eventDescriptionLanguageEn,
    also: m.eventDescriptionAlsoEn,
    heading: m.eventDescriptionInEn,
  },
}

const sameText = (a: string, b: string) => richTextToPlainText(a) === richTextToPlainText(b)

export function EventDescriptionLanguages({
  form,
}: {
  // biome-ignore lint/suspicious/noExplicitAny: TanStack Form API
  form: any
}) {
  const { description, language, translations } = useStore(
    form.store,
    (state: unknown) => {
      const { values } = state as DescriptionFormState
      return {
        description: values.description ?? '',
        language: values.descriptionLanguage ?? 'de',
        translations: values.descriptionTranslations ?? {},
      }
    },
    shallow,
  )
  const other: EventDescriptionLanguage = language === 'de' ? 'en' : 'de'
  const translation = translations[other]
  const [enabled, setEnabled] = useState(() => Boolean(translation?.trim()))
  const [translatedFrom, setTranslatedFrom] = useState<string | null>(() =>
    translation?.trim() ? description : null,
  )
  const { data: business } = useCurrentBusiness()
  const translate = useTranslateDescription(business?.id ?? '')
  const switchId = useId()
  const editorId = useId()

  const setTranslation = (html: string | undefined) =>
    form.setFieldValue('descriptionTranslations', html === undefined ? {} : { [other]: html })

  const runTranslate = () => {
    if (!richTextToPlainText(description)) {
      toast.error(m.eventDescriptionTranslateNeedsText())
      return
    }
    const source = description
    translate.mutate(
      { html: source, from: language, to: other },
      {
        onSuccess: ({ html }) => {
          setTranslation(html)
          setTranslatedFrom(source)
        },
        onError: (error) => {
          toast.error(getErrorMessageFromError(error))
        },
      },
    )
  }

  const toggle = (next: boolean) => {
    setEnabled(next)
    if (!next) {
      setTranslation(undefined)
      setTranslatedFrom(null)
    } else if (translation === undefined) setTranslation('')
  }

  const empty = !translation?.trim()
  const stale =
    enabled &&
    !empty &&
    translatedFrom !== null &&
    !translate.isPending &&
    !sameText(description, translatedFrom)

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="text-sm font-medium">{m.eventDescriptionLanguages()}</span>
        <fieldset
          disabled={enabled}
          title={enabled ? m.eventDescriptionLanguageLocked() : undefined}
          className="min-w-0 disabled:opacity-60"
        >
          <SegmentedControl
            size="sm"
            label={m.eventDescriptionOriginalLanguage()}
            value={language}
            options={[
              { value: 'de', label: LANGUAGE_COPY.de.name() },
              { value: 'en', label: LANGUAGE_COPY.en.name() },
            ]}
            onChange={(next) => form.setFieldValue('descriptionLanguage', next)}
          />
        </fieldset>
        <div className="flex items-center gap-2">
          <Switch
            id={switchId}
            checked={enabled}
            onCheckedChange={toggle}
            disabled={translate.isPending}
          />
          <Label htmlFor={switchId} className="font-normal">
            {LANGUAGE_COPY[other].also()}
          </Label>
        </div>
      </div>

      {enabled && translation !== undefined && (
        <div className="space-y-2">
          <div className="flex min-h-8 items-center justify-between gap-2">
            <Label htmlFor={editorId}>{LANGUAGE_COPY[other].heading()}</Label>
            {empty && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={runTranslate}
                disabled={translate.isPending}
              >
                {translate.isPending ? (
                  <PulsifyLoader className="size-4" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                {translate.isPending
                  ? m.eventDescriptionTranslating()
                  : m.eventDescriptionTranslateForMe()}
                <CreditCostTag cost={EVENT_TRANSLATION_CREDIT_COST} />
              </Button>
            )}
            {!empty && (stale || translate.isPending) && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={runTranslate}
                disabled={translate.isPending}
              >
                {translate.isPending ? (
                  <PulsifyLoader className="size-4" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {m.eventDescriptionRetranslate()}
                <CreditCostTag cost={EVENT_TRANSLATION_CREDIT_COST} />
              </Button>
            )}
          </div>
          {empty && !translate.isPending && (
            <p className="text-xs text-muted-foreground">{m.eventDescriptionWriteOrTranslate()}</p>
          )}
          {stale && (
            <p className="text-xs text-muted-foreground">{m.eventDescriptionTranslationStale()}</p>
          )}
          <RichTextEditor
            id={editorId}
            value={translation}
            onChange={(html) => setTranslation(html)}
            ariaLabel={LANGUAGE_COPY[other].heading()}
          />
        </div>
      )}
    </div>
  )
}
